/**
 * Which unit a given exercise's distance is shown and typed in.
 *
 * Storage is metres, always. This decides only what the box is labelled and
 * therefore what a typed number means — which is the whole ballgame, because
 * getting it wrong doesn't error, it records a farmer's carry as eighteen miles
 * and awards a distance record nobody can ever beat.
 *
 * Two layers:
 *
 *   - The account default (`distance_pref`), which syncs, lives on the server
 *     and is set in Settings.
 *   - A per-exercise override, set by the toggle on the log form and stored in
 *     ONE Dexie meta row as a map. Local-only and deliberately so: it is a
 *     display habit, not data, and giving it a sync entity would mean a
 *     migration, a conflict rule and a tombstone for the sake of remembering
 *     that you like sprints in yards. The cost is that a new device starts on
 *     the account default until you flip it again.
 *
 * One meta row rather than one per exercise, because every screen that renders
 * a list of different exercises needs the whole map at once.
 */

import { useLiveQuery } from 'dexie-react-hooks'

import { distanceScaleOf, getMeta, setMeta, type Exercise } from '../db'
import {
  resolveDistanceUnit,
  type DistancePref,
  type DistanceUnit,
  type Unit,
} from './units'

export const DISTANCE_UNITS_KEY = 'lift.distanceUnits'

/** exercise id -> the unit chosen for it. */
export type DistanceOverrides = Record<number, DistanceUnit>

export async function getDistanceOverrides(): Promise<DistanceOverrides> {
  return getMeta<DistanceOverrides>(DISTANCE_UNITS_KEY, {})
}

export async function setDistanceOverride(
  exerciseId: number,
  du: DistanceUnit | null,
): Promise<void> {
  const current = await getDistanceOverrides()
  const next = { ...current }
  // Clearing removes the key rather than storing a null, so "no override" has
  // exactly one representation and `resolveDistanceUnit` never has to tell a
  // deliberate null from an absent one.
  if (du === null) delete next[exerciseId]
  else next[exerciseId] = du
  await setMeta(DISTANCE_UNITS_KEY, next)
}

/**
 * The override map, live. `{}` while loading, which is correct rather than
 * merely convenient: an empty map means "no overrides", the answer is the
 * account default, and nothing flickers when the real map lands a frame later.
 */
export function useDistanceOverrides(): DistanceOverrides {
  return useLiveQuery(getDistanceOverrides, [], {} as DistanceOverrides)
}

/** The unit in force for one exercise. Pure, so lists can call it per row. */
export function unitFor(
  exercise: Exercise | null | undefined,
  opts: { unit: Unit; pref: DistancePref; overrides?: DistanceOverrides },
): DistanceUnit {
  const scale = distanceScaleOf(exercise)
  const override = exercise ? opts.overrides?.[exercise.id] : undefined
  return resolveDistanceUnit({ scale, pref: opts.pref, unit: opts.unit, override })
}
